import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, TrendingUp, Globe, Bot, Truck, Zap } from 'lucide-react';

const articleData = {
  4: {
    title: "Looking Ahead 2026: From Volatility to Velocity in Global Logistics",
    category: "Strategy",
    icon: TrendingUp,
    color: "rgb(168,85,247)",
    image: "https://images.unsplash.com/photo-1761195689615-9469b65dac01?w=1200&auto=format&fit=crop&q=80",
    source: "LinkedIn",
    sourceUrl: "https://www.linkedin.com/pulse/looking-ahead-2026-from-volatility-velocity-plus-kz8je/",
    readTime: "8 min read",
    content: {
      intro: "As 2026 begins, it's impossible to overlook just how much the turbulence of 2025 reshaped the logistics industry. The final quarter alone brought a cocktail of shifting capacity, unanticipated geopolitical repercussions, and abrupt changes in consumer behaviour.",
      sections: [
        {
          title: "From Volatility to Velocity",
          content: "This year, the focus shifts to turning that resilience into momentum, a deliberate transition from volatility to velocity. It's not just about moving more quickly, but also about moving with greater wisdom, with sharper decision-making and better visibility directing each step."
        },
        {
          title: "Adaptability as Survival",
          content: "One of the most obvious lessons from 2025 is that adaptability is now a necessity for survival rather than a luxury. Organizations that embraced adaptability managed to stay ahead of disruption."
        },
        {
          title: "Enhanced Visibility",
          content: "Visibility also emerged as a crucial component last year. Companies that utilised real-time tracking, predictive ETAs, and improved upstream intelligence were able to prevent delays and take proactive action."
        }
      ]
    }
  },
  5: {
    title: "South Korea Accelerates Arctic Shipping Push with September Trial Run",
    category: "Infrastructure", 
    icon: Globe,
    color: "rgb(34,197,94)",
    image: "https://images.unsplash.com/photo-1524351364533-7e6bbb2447a6?w=1200&auto=format&fit=crop&q=80",
    source: "gCaptain",
    sourceUrl: "https://gcaptain.com/south-korea-accelerates-arctic-shipping-push-with-september-trial-run-to-europe/",
    readTime: "6 min read",
    content: {
      intro: "South Korea plans to send a containership through Arctic waters from Busan to Rotterdam this September, marking its first concrete step toward opening a faster northern trade route between Asia and Europe.",
      sections: [
        {
          title: "Strategic Arctic Initiative",
          content: "The government intends to deploy a 3,000-TEU vessel on the trial voyage and support the construction of icebreakers and other polar-class ships, according to Acting Oceans Minister Kim Sung-bum."
        },
        {
          title: "China Sets the Pace",
          content: "South Korea is entering an Arctic shipping arena already being reshaped by China. Chinese operators completed 14 container voyages between Asia and Europe via the Northern Sea Route in 2025."
        },
        {
          title: "Western Caution Persists",
          content: "Most Western container lines remain on the sidelines. Maersk carried out a single Arctic trial in 2018 but later ruled out regular services, citing environmental concerns and uncertain economics."
        }
      ]
    }
  },
  6: {
    title: "Global Logistics Robots Market Reaches $43.5B by 2035",
    category: "Automation",
    icon: Bot,
    color: "rgb(59,130,246)",
    image: "https://images.unsplash.com/photo-1583911300103-0aaeff286030?w=1200&auto=format&fit=crop&q=80",
    source: "Market.us",
    sourceUrl: "https://market.us/report/global-logistics-robots-market/",
    readTime: "10 min read",
    content: {
      intro: "The Global Logistics Robots Market size is expected to be worth around USD 43.5 Billion by 2035, from USD 9.7 Billion in 2025, growing at a CAGR of 16.2% during the forecast period.",
      sections: [
        {
          title: "Market Drivers",
          content: "The market demonstrates robust expansion driven by e-commerce growth and increasing demand for warehouse automation solutions. Organizations are actively pursuing automation investments to address labor shortages."
        },
        {
          title: "Technology Segments",
          content: "Automated Guided Vehicles dominate with 36.6% market share due to their proven reliability and established infrastructure compatibility. Autonomous Mobile Robots represent the emerging technology segment."
        },
        {
          title: "Regional Analysis",
          content: "Asia Pacific leads the global logistics robots market with a commanding share of 47.5%, valued at USD 4.6 billion, driven by rapid e-commerce growth and extensive manufacturing activities."
        }
      ]
    }
  },
  7: {
    title: "Boston Dynamics & Google DeepMind Form AI Partnership for Humanoid Robots",
    category: "AI Partnership",
    icon: Zap,
    color: "rgb(168,85,247)",
    image: "https://images.unsplash.com/photo-1696258686454-60082b2c33e2?w=1200&auto=format&fit=crop&q=80",
    source: "Boston Dynamics",
    sourceUrl: "https://bostondynamics.com/blog/boston-dynamics-google-deepmind-form-new-ai-partnership/",
    readTime: "5 min read",
    content: {
      intro: "Boston Dynamics and Google DeepMind have formed a new AI partnership designed to bring a new era of artificial intelligence to humanoid robots, announced at CES 2026 in Las Vegas.",
      sections: [
        {
          title: "Strategic Partnership Goals",
          content: "The strategic partnership will focus on enabling humanoids to complete a wide variety of industrial tasks and is expected to become a driving force of manufacturing transformation, beginning in the automotive industry."
        },
        {
          title: "Technology Integration",
          content: "The two teams aim to integrate cutting edge Gemini Robotics AI foundation models with Boston Dynamics' new Atlas® robots, combining athletic intelligence with advanced AI capabilities."
        },
        {
          title: "Industry Impact",
          content: "Long known for its robots' athleticism, Boston Dynamics announced its intention to build a commercial humanoid in 2024, after AI advances accelerated robot training and deployment capabilities."
        }
      ]
    }
  },
  8: {
    title: "Amazon Expands Robotics Innovation with Strategic Acquisitions",
    category: "E-commerce",
    icon: Truck,
    color: "rgb(59,130,246)",
    image: "https://images.unsplash.com/photo-1633174524827-db00a6b7bc74?w=1200&auto=format&fit=crop&q=80",
    source: "The Robot Report",
    sourceUrl: "https://www.therobotreport.com/amazon-acquires-rightbot-adds-robotics-delivery-packaging-innovation-team/",
    readTime: "4 min read",
    content: {
      intro: "Amazon continues to expand its robotics capabilities through strategic acquisitions focused on delivery and packaging innovation, adding new automation technologies to its logistics network.",
      sections: [
        {
          title: "Acquisition Strategy",
          content: "Amazon's latest acquisitions target companies developing advanced robotics solutions for last-mile delivery and automated packaging systems, enhancing the company's end-to-end automation capabilities."
        },
        {
          title: "Innovation Focus",
          content: "The acquisitions focus on robotics delivery and packaging innovation teams, bringing specialized expertise in autonomous systems and intelligent automation to Amazon's operations."
        },
        {
          title: "Market Impact",
          content: "These strategic moves position Amazon to further accelerate automation across its fulfillment network, potentially setting new industry standards for logistics efficiency and customer service."
        }
      ]
    }
  },
  9: {
    title: "PepsiCo Leverages 3D Digital Twin Technology for Supply Chain Optimization",
    category: "Digital Twin",
    icon: Zap,
    color: "rgb(34,197,94)",
    image: "https://images.unsplash.com/photo-1717386255773-a456c611dc4e?w=1200&auto=format&fit=crop&q=80",
    source: "Consumer Goods",
    sourceUrl: "https://consumergoods.com/pepsico-get-ahead-supply-chain-challenges-3d-digital-twin-tech",
    readTime: "7 min read",
    content: {
      intro: "PepsiCo's move toward a digital-first planning strategy uses physics-based digital twin and AI technology, allowing tools to function as 'co-designers' that can simulate, validate and optimize plant and warehousing facility layouts.",
      sections: [
        {
          title: "Real-Time Digital Twins",
          content: "The partnership combines 2D and 3D digital twin data from Siemens with NVIDIA computer vision-enabled Omniverse libraries, recreating every machine, conveyor, pallet route and operator path in a digital setting."
        },
        {
          title: "Impressive Results",
          content: "PepsiCo is already reporting a 20% increase in throughput, faster design cycles, nearly 100% design validation, and 10% to 15% reductions in capital expenditure due to increased capacity discovered in virtual environments."
        },
        {
          title: "Global Expansion",
          content: "Once early testing in the U.S. is complete, the company plans to scale efforts globally, starting with its biggest markets in 2026 and 2027, building toward an intelligent ecosystem of facilities."
        }
      ]
    }
  }
};

const ArticleTemplate = () => {
  const { id } = useParams();
  const articleId = parseInt(id || '4');
  const article = articleData[articleId as keyof typeof articleData];

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = article.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge 
              className="text-white mr-4"
              style={{ backgroundColor: article.color }}
            >
              <IconComponent className="h-3 w-3 mr-1" />
              {article.category}
            </Badge>
            <span className="text-sm text-gray-600">{article.source}</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            January 8, 2026
            <span className="mx-2">•</span>
            <span>{article.readTime}</span>
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {article.title}
          </h1>
          
          <img 
            src={article.image}
            alt={article.title}
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {article.content.intro}
            </p>

            {article.content.sections.map((section, index) => (
              <div key={index}>
                <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
                  {section.title}
                </h2>
                <p className="mb-6">
                  {section.content}
                </p>
              </div>
            ))}

            <div className="bg-blue-50 p-6 rounded-lg mt-8">
              <p className="text-sm text-gray-700">
                <strong>This article provides insights into the latest developments in {article.category.toLowerCase()} affecting the global logistics industry.</strong>
              </p>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              This article is based on information from {article.source}.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              For the complete original report and additional details, please visit the source.
            </p>
            {article.sourceUrl && (
              <a 
                href={article.sourceUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block"
              >
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  Read Original Article
                </Button>
              </a>
            )}
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to All Articles
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default ArticleTemplate;